import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePremisesStore } from "../../store/store";
import { DotLoader } from "../../components/loader/Loader";
import { premisesApiResponse } from "../../types/api";
import TrendChartsPdfDownload from "./TrendChartsPdfDownload";
import AllPremisesReportDownload from "./AllPremisesReportDownload";
import { Download } from "lucide-react";
import axiosInstance from "../../common/axiosInstance";

// ─── ViewSummaryButton ────────────────────────────────────────────────────────

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const ViewSummaryButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  const handleExportExcel = async (month: number, year: number) => {
    setIsExporting(true);
    try {
      const response = await axiosInstance.get("api/reports/fire-safety-scorecards/download-summary", {
        params: { month: month + 1, year },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `Fire_Safety_Summary_${year}_${MONTH_NAMES[month]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setIsOpen(false);
    } catch (err: any) {
      console.error(err);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-4 py-2 border-2 border-[#00A8A5] text-[#00A8A5] rounded-lg hover:bg-[#00A8A5] hover:text-white transition-colors text-sm font-semibold"
      >
        <Download className="w-4 h-4" />
        View Summary of all premises
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          {/* Modal card */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-[#00A8A5] px-6 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Download className="w-5 h-5 text-white" />
                <h3 className="text-white font-semibold text-base">Download Summary Sheet</h3>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white/80 hover:text-white transition-colors"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-4 flex flex-col gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Month &amp; Year
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#00A8A5] focus:border-transparent transition appearance-none bg-white"
                  >
                    {MONTH_NAMES.map((month, idx) => (
                      <option key={idx} value={idx}>{month}</option>
                    ))}
                  </select>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#00A8A5] focus:border-transparent transition appearance-none bg-white"
                  >
                    {Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-gray-400 mt-1.5">
                  All premises will be included. Premises with no data for the selected month will show N/A.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 pb-4 flex items-center justify-end gap-3">
              <button
                onClick={() => setIsOpen(false)}
                className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleExportExcel(selectedMonth, selectedYear)}
                disabled={isExporting}
                className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-[#00A8A5] hover:bg-[#008A87] rounded-lg transition-colors disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                {isExporting ? "Downloading..." : "Download"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─── PremiseCard ─────────────────────────────────────────────────────────────

interface PremiseCardProps {
  premise: premisesApiResponse;
  onClick: (premiseId: string) => void;
}

const PremiseCard: React.FC<PremiseCardProps> = ({ premise, onClick }) => {
  const premiseId = premise._id || premise.id;

  const locationParts: string[] = [];
  if (premise.addressLine1) locationParts.push(premise.addressLine1);
  if (premise.townCity) locationParts.push(premise.townCity);
  if (premise.region) locationParts.push(premise.region);
  const locationText = locationParts.join(", ");

  return (
    <div
      data-testid="premise-card"
      onClick={() => onClick(premiseId)}
      className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl hover:scale-[1.02] transition-all duration-200 cursor-pointer flex flex-col"
    >
      {/* Teal gradient top bar */}
      <div className="bg-gradient-to-r from-[#00A8A5] to-[#007A78] px-5 py-4 flex items-center gap-3">
        {/* Fire / building icon */}
        <div className="bg-white/20 rounded-lg p-2 flex-shrink-0">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-6 h-6 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.8}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 21h18M3 10h18M3 7l9-4 9 4M4 10v11M20 10v11M8 14v3m4-3v3m4-3v3"
            />
          </svg>
        </div>
        <span className="text-white text-xs font-medium uppercase tracking-wider opacity-80">
          Premises
        </span>
      </div>

      {/* Card body */}
      <div className="px-5 py-4 flex flex-col flex-1">
        <h3 className="text-gray-900 font-bold text-lg leading-snug mb-2 line-clamp-2">
          {premise.premisesName}
        </h3>

        {locationText ? (
          <div className="flex items-start gap-1.5 text-gray-500 text-sm mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#00A8A5]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span className="line-clamp-2">{locationText}</span>
          </div>
        ) : (
          <div className="mb-4" />
        )}

        {/* CTA */}
        <div className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-between">
          <span className="text-[#00A8A5] text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
            View Reports
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </span>
          {premise.isStatus !== undefined && (
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${premise.isStatus
                ? "bg-green-100 text-green-700"
                : "bg-gray-100 text-gray-500"
                }`}
            >
              {premise.isStatus ? "Active" : "Inactive"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── MonitoringPage ───────────────────────────────────────────────────────────

const MonitoringPage: React.FC = () => {
  const navigate = useNavigate();
  const { allPremises, fetchPremises, loading } = usePremisesStore();
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!allPremises.length) {
      fetchPremises();
    }
  }, []);

  const filteredPremises = allPremises.filter((p) =>
    p.premisesName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCardClick = (premiseId: string) => {
    navigate(`/fire-system/monitoring/${premiseId}`);
  };

  return (
    <div className="w-full px-6 mt-20 pb-10">
      {/* Page header */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Monitoring</h1>
          <p className="text-gray-500 text-sm mt-1">
            Select a premises to view monthly fire safety reports
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <ViewSummaryButton />
          <TrendChartsPdfDownload premises={allPremises} />
          <AllPremisesReportDownload premises={allPremises} />
        </div>
      </div>

      {/* Search bar */}
      <div className="mb-6 max-w-md">
        <div className="relative">
          <span className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z"
              />
            </svg>
          </span>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search premises..."
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00A8A5]/40 focus:border-[#00A8A5] bg-white shadow-sm transition"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex justify-center items-center py-24">
          <DotLoader />
        </div>
      )}

      {/* Premises grid */}
      {!loading && filteredPremises.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filteredPremises.map((premise) => (
            <PremiseCard
              key={premise._id || premise.id}
              premise={premise}
              onClick={handleCardClick}
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && filteredPremises.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="bg-gray-100 rounded-full p-5 mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-10 h-10 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 21h18M3 10h18M3 7l9-4 9 4M4 10v11M20 10v11M8 14v3m4-3v3m4-3v3"
              />
            </svg>
          </div>
          <h3 className="text-gray-700 font-semibold text-lg mb-1">
            {searchQuery ? "No premises found" : "No premises available"}
          </h3>
          <p className="text-gray-400 text-sm max-w-xs">
            {searchQuery
              ? `No premises match "${searchQuery}". Try a different search term.`
              : "There are no premises configured yet. Please add premises to get started."}
          </p>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="mt-4 text-[#00A8A5] text-sm font-medium hover:underline"
            >
              Clear search
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MonitoringPage;
